import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { Trophy, Coins, Flag, TextT, Ticket } from "@phosphor-icons/react";

const PRIZE_ICON = {
  credits:              { Icon: Coins,  tint: "text-blue-600 dark:text-blue-400" },
  banner_impressions:   { Icon: Flag,   tint: "text-fuchsia-600 dark:text-fuchsia-400" },
  text_ad_impressions:  { Icon: TextT,  tint: "text-cyan-600 dark:text-cyan-400" },
};

const PAGE_SIZE = 25;

/**
 * Members → Quick Actions → Surf Drawn Winners.
 *
 * Reads /api/draws (public feed) and shows every past drawing:
 * winner, prize type, amount, ticket count at time of draw, and date/time.
 */
export default function SurfDrawnWinners() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async (off) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/draws?limit=${PAGE_SIZE}&offset=${off}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setOffset(off);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(0); }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const page = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Rewards</div>
        <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white inline-flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
          <Trophy size={30} weight="fill" className="text-amber-500" />
          Surf Drawn Winners
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
          Every past drawing from the Surf Drawn Tickets pool. Tickets are earned from milestone prizes on your surf progress. Each drawing picks a random ticket holder — the more tickets you hold, the better your odds.
        </p>
      </div>

      <div className="rounded-2xl border border-amber-200 dark:border-amber-500/30 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-500/5 dark:to-orange-500/5 overflow-hidden">
        <div className="p-5 border-b border-amber-200/70 dark:border-amber-500/20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center shadow-sm">
              <Ticket size={20} weight="fill" />
            </div>
            <div>
              <div className="font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>All winners</div>
              <div className="text-xs text-slate-500">Newest first</div>
            </div>
          </div>
          <div className="text-xs font-black uppercase tracking-widest text-slate-500" data-testid="draws-total">{total} draw{total !== 1 ? "s" : ""}</div>
        </div>

        <div className="divide-y divide-amber-200/50 dark:divide-amber-500/10">
          {loading && <div className="p-6 text-sm text-slate-500 text-center">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-500">
              <Trophy size={30} weight="duotone" className="mx-auto text-amber-400 mb-2" />
              No draws have been held yet. Earn Surf Drawn Tickets from milestone prizes to enter the next one!
            </div>
          )}
          {items.map((d) => {
            const meta = PRIZE_ICON[d.prize_type] || { Icon: Trophy, tint: "text-amber-500" };
            const Icon = meta.Icon;
            return (
              <div key={d.id} data-testid={`draw-row-${d.id}`} className="p-4 flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center font-black shrink-0 overflow-hidden">
                  {d.winner_avatar_url ? (
                    <img src={d.winner_avatar_url} alt="" className="h-12 w-12 object-cover" />
                  ) : (
                    (d.winner_username || "?").slice(0, 1).toUpperCase()
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-slate-900 dark:text-white truncate">@{d.winner_username}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {new Date(d.drawn_at).toLocaleString()} · {d.tickets_at_draw?.toLocaleString?.() ?? d.tickets_at_draw} tickets · {d.entrant_count} entrants
                  </div>
                  {d.name && <div className="text-xs font-bold text-amber-700 dark:text-amber-300 mt-0.5">{d.name}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className={`inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest ${meta.tint}`}>
                    <Icon size={14} weight="fill" /> {d.prize_type_label}
                  </div>
                  <div className="text-lg font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }} data-testid={`draw-amount-${d.id}`}>
                    +{Number(d.prize_amount).toLocaleString()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {total > PAGE_SIZE && (
          <div className="p-3 flex items-center justify-between border-t border-amber-200/70 dark:border-amber-500/20">
            <button
              data-testid="draws-prev"
              disabled={offset === 0 || loading}
              onClick={() => load(Math.max(0, offset - PAGE_SIZE))}
              className="text-xs font-bold rounded-full px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 disabled:opacity-50"
            >
              Prev
            </button>
            <div className="text-xs text-slate-500">Page {page} of {totalPages}</div>
            <button
              data-testid="draws-next"
              disabled={offset + PAGE_SIZE >= total || loading}
              onClick={() => load(offset + PAGE_SIZE)}
              className="text-xs font-bold rounded-full px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
